import cn from 'classnames';
import Image from "next/legacy/image";
import { getThemedImage } from '@components/layout/get-themed-image';
import type { FC } from 'react';
import { useUI } from '@contexts/ui.context';
import { Product } from '@framework/types';
import { useState, useEffect } from 'react';
import { getCurrentLocationAndLanguage } from '@utils/location';
import { generateCartItem } from '@utils/generate-cart-item';
import { useCartQuery } from '@framework/cart/all-cart';
import { useGuestCartListQuery } from '@framework/cart/all-cart';
import ProductWishIcon from '@components/icons/product-wish-icon';
import RatingDisplay from '@components/common/rating-display';
import { useTranslation } from 'next-i18next';
import CartIcon from '@components/icons/cart-icon';
import { useCart } from '@contexts/cart/cart.context';
import Counter from '@components/common/counter';
import { useRemoveFromCartMutation, useUpdateCartQuantityMutation } from '@framework/cart/all-cart';
interface ProductProps {
  product: Product;
  discount?: number;
  className?: string;
  contactClassName?: string;
  imageContentClassName?: string;
  variant?: 'grid' | 'gridSlim' | 'list' | 'listSmall' | 'gridModern' | 'gridModernWide' | 'gridTrendy' | 'rounded' | 'circle';
  imgWidth?: number | string;
  imgHeight?: number | string;
  imgLoading?: 'eager' | 'lazy';
  hideProductDescription?: boolean;
  showCategory?: boolean;
  showRating?: boolean;
  bgTransparent?: boolean;
  bgGray?: boolean;
  demoVariant?: 'ancient';
  disableBorderRadius?: boolean;
}
const ProductCard: FC<ProductProps> = ({
  product,
  discount = false,
  className = '',
  contactClassName = '',
  imageContentClassName = '',
  variant = 'list',
  imgWidth = 340,
  imgHeight = 440,
  imgLoading,
  hideProductDescription = false,
  showCategory = false,
  showRating = false,
  bgTransparent = false,
  bgGray = false,
  demoVariant,
  disableBorderRadius = false
}) => {
  const {
    openModal,
    setModalView,
    setModalData,
    isAuthorized
  } = useUI();
  const {
    location
  } = getCurrentLocationAndLanguage();
  const [quantity, setQuantity] = useState(1);
  const [attributes, _setAttributes] = useState({});
  const [_addToCartLoader, setAddToCartLoader] = useState(false);
  const [viewCartBtn, setViewCartBtn] = useState(false);
  const isSelected = true; // default value for product card
  const {
    t
  } = useTranslation('common');
  const {
    cart,
    addItemToCart
  } = useCart() as any;
  const {
    data: freshCart,
    refetch
  } = useCartQuery();
  const isGuestKg = location === 'kg' && !isAuthorized;
  const {
    data: guestCart,
    refetch: refetchGuest
  } = useGuestCartListQuery({
    enabled: isGuestKg
  });
  const {
    mutate: updateCartQuantity
  } = useUpdateCartQuantityMutation();
  const {
    mutate: removeItemFromCart
  } = useRemoveFromCartMutation();
  const getCartItems = () => {
    if (isGuestKg) return guestCart?.carts?.[0]?.items ?? [];
    return freshCart?.items ?? cart?.items ?? [];
  };
  const refetchCart = isGuestKg ? refetchGuest : refetch;
  const handleIncrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    updateCartQuantity({
      cartId: cart?.id,
      productCode: product.productCode,
      quantity: newQuantity
    }, {
      onSuccess: () => {
        refetchCart();
      }
    });
  };
  const handleDecrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (quantity > 1) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      updateCartQuantity({
        cartId: cart?.id,
        productCode: product.productCode,
        quantity: newQuantity
      }, {
        onSuccess: () => {
          refetchCart();
        }
      });
    } else {
      setQuantity(0);
      setViewCartBtn(false);
      removeItemFromCart({
        cartId: cart?.id,
        productCode: product.productCode
      }, {
        onSuccess: () => {
          refetchCart();
        }
      });
    }
  };
  async function addToCart() {
    if (location !== 'kg' && !isAuthorized) {
      setModalView('LOGIN_VIEW');
      openModal();
      return;
    }
    if (!isSelected) return;
    await refetchCart();
    const items = getCartItems();
    const existingItem = items.find((item: any) => item.productCode === product.productCode);
    const newQuantity = existingItem ? existingItem.quantity + 1 : 1;
    setAddToCartLoader(true);
    setTimeout(() => {
      setAddToCartLoader(false);
      setViewCartBtn(true);
    }, 600);
    if (existingItem) {
      setQuantity(newQuantity);
      updateCartQuantity({
        cartId: isGuestKg ? null : cart?.id,
        productCode: product.productCode,
        quantity: newQuantity
      });
    } else {
      const item = generateCartItem(product, attributes);
      addItemToCart(item, newQuantity);
      setQuantity(newQuantity);
    }
  }
  useEffect(() => {
    if (!isGuestKg) {
      const activeCart = freshCart ?? cart;
      const currentItem = activeCart?.items?.find((item: any) => item.productCode === product.productCode);
      if (currentItem) {
        if (currentItem.quantity !== quantity) {
          setQuantity(currentItem.quantity);
        }
        setViewCartBtn(true);
      } else {
        setViewCartBtn(false);
        setQuantity(1);
      }
    }
  }, [freshCart, cart, product.productCode, isGuestKg]);
  useEffect(() => {
    if (isGuestKg) {
      const fetchAndSync = async () => {
        const updatedCart = await refetchGuest();
        const data: any = updatedCart?.data;
        const items = data?.carts?.[0]?.items ?? [];
        const currentItem = items.find((item: any) => item.productCode === product.productCode);
        if (currentItem && currentItem.quantity > 0) {
          setQuantity(currentItem.quantity);
          setViewCartBtn(true);
        } else {
          setQuantity(1);
          setViewCartBtn(false);
        }
      };
      fetchAndSync();
    }
  }, [product.productCode, guestCart, isGuestKg]);
  const placeholderImageLight = '/assets/placeholder/products/product-thumbnail.svg';
  const placeholderImageDark = '/assets/placeholder/products/product-thumbnail-dark.svg';
  function handlePopupView() {
    setModalData({
      data: product
    });
    setModalView('PRODUCT_VIEW');
    return openModal();
  }
  const handleWishlist = (e: any) => {
    e.stopPropagation();
  };
  return <div className={cn(`group box-border overflow-hidden flex ${!disableBorderRadius && 'rounded-md'} cursor-pointer`, {
    'ltr:pr-0 rtl:pl-0 pb-2 lg:pb-3 flex-col items-start transition duration-200 ease-in-out transform hover:-translate-y-1 md:hover:-translate-y-1.5 hover:shadow-product': variant === 'grid' || variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy',
    ' bg-white dark:bg-zinc-950': variant === 'grid' && !bgGray || variant === 'gridModern' && !bgGray || variant === 'gridModernWide' && !bgGray || variant === 'gridTrendy' && !bgGray || variant === 'gridSlim' && !bgGray,
    'bg-gray-200': variant === 'list' || bgGray,
    'ltr:pr-0 rtl:pl-0 md:pb-1 flex-col items-start': variant === 'gridSlim',
    'items-center border border-gray-100 transition duration-200 ease-in-out transform hover:-translate-y-1 hover:shadow-listProduct': variant === 'listSmall',
    'flex-row items-center transition-transform ease-linear ltr:pr-2 rtl:pl-2 ltr:lg:pr-3 rtl:lg:pl-3 ltr:2xl:pr-4 rtl:2xl:pl-4': variant === 'list',
    '!bg-transparent': variant === 'grid' && bgTransparent === true
  }, className)} onClick={handlePopupView} role='button' title={product?.name} data-sentry-component="ProductCard" data-sentry-source-file="product-card.tsx">
      <div className={cn('flex', {
      'mb-3 md:mb-3.5': variant === 'grid',
      'mb-3 md:mb-3.5 pb-0': variant === 'gridSlim',
      'flex-shrink-0 w-32 sm:w-44 md:w-36 lg:w-44': variant === 'listSmall',
      'mb-3 md:mb-3.5 relative': variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy'
    }, imageContentClassName)}>
        {product?.photo?.x318 ? <Image src={product.photo.x318} width={Number(imgWidth)} height={Number(imgHeight)} loading={imgLoading} quality={100} alt={product?.name || 'Product Image'} objectFit='contain' className={cn(`bg-gray-300 dark:bg-zinc-800 object-cover ${!disableBorderRadius && 'rounded-s-md'}`, {
        'w-full transition duration-200 ease-in': variant === 'grid' || variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy',
        'rounded-md group-hover:rounded-b-none': variant === 'grid' && !disableBorderRadius || variant === 'gridModern' && !disableBorderRadius || variant === 'gridModernWide' && !disableBorderRadius || variant === 'gridTrendy' && !disableBorderRadius,
        'rounded-md transition duration-150 ease-linear transform group-hover:scale-105': variant === 'gridSlim',
        'rounded-s-md transition duration-200 ease-linear transform group-hover:scale-105': variant === 'list'
      })} /> : getThemedImage(placeholderImageLight, placeholderImageDark, product?.name || 'Product Image', {
        width: Number(imgWidth),
        height: Number(imgHeight),
        loading: imgLoading,
        quality: 100,
        objectFit: 'contain',
        className: cn(`bg-gray-300 dark:bg-zinc-800 object-cover ${!disableBorderRadius && 'rounded-s-md'}`, {
          'w-full transition duration-200 ease-in': variant === 'grid' || variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy',
          'rounded-md group-hover:rounded-b-none': variant === 'grid' && !disableBorderRadius || variant === 'gridModern' && !disableBorderRadius || variant === 'gridModernWide' && !disableBorderRadius || variant === 'gridTrendy' && !disableBorderRadius,
          'rounded-md transition duration-150 ease-linear transform group-hover:scale-105': variant === 'gridSlim',
          'rounded-s-md transition duration-200 ease-linear transform group-hover:scale-105': variant === 'list'
        })
      })}
        {/* Left badges */}
        <div className='absolute top-3.5 md:top-5 3xl:top-7 start-3.5 md:start-5 3xl:start-7 flex flex-col gap-y-1 items-start'>
          {discount && (variant === 'gridModernWide' || variant === 'gridModern' || variant === 'gridTrendy') && <span className='bg-heading text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
                <p>
                  <span className='sm:hidden'>-</span>
                 - {discount} <span className='hidden sm:inline'></span>
                </p>
              </span>}
            {product.isNew && <span className='bg-f15MedPrimary text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
              <p>
              {t('text-new') || 'New'}
              </p>
            </span>}
          {product.isPopular && <span className='bg-c9Primary text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
              <p>
              {t('text-popular') || 'Popular'}
              </p>
            </span>}
          {product.hasPromotion && <span className='bg-red-600 text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
              <p>
              {t('text-promotion') || 'Promotion'}
              </p>
            </span>}
          {product.amount === 0 && <span className='bg-red-600 text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
                  {t('text-out-of-stock') || 'Out of stock'}
                </span>}
           {product.expirationDate && <span className='bg-olive text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1.5 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
              <p>
                <span className='hidden sm:inline'></span>
                {t('text-expirationdate-productgrid', {
              expirationDate: product.expirationDate
            })}
              </p>
            </span>}
          {/* <span className='bg-f15BeginPrimary text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1.5 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
            <p>
              Promotions <span className='hidden sm:inline'>Arrival</span>
            </p>
           </span> */}
        </div>
        {/* Right badges */}
        <div className='absolute top-3.5 md:top-5 3xl:top-7 end-3.5 md:end-5 3xl:end-7 flex flex-col gap-y-1 items-end'>
          <span className='bg-[#000] text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
            <p>#{product.productCode}</p>
          </span>
          <span className='bg-[#000] text-white text-10px md:text-xs leading-5 rounded-md inline-block px-1.5 sm:px-1.5 xl:px-2 py-0.5 sm:py-1'>
            <p>
              {product.cc} <span className='hidden sm:inline'>CC</span>
            </p>
          </span>
        </div>
        {variant === 'gridModernWide' && <div className='absolute end-2 sm:end-3 bottom-6 space-y-2 w-[32px] sm:w-[42px] lg:w-[52px]' onClick={handleWishlist}>
            <ProductWishIcon className='transition ease-in duration-300 sm:opacity-0 group-hover:opacity-100 delay-200 w-full bg-white dark:bg-zinc-950 rounded-md' />
          </div>}
      </div>
      <div className={cn('w-full overflow-hidden p-2', {
      'md:px-2.5 xl:px-4': variant === 'grid',
      'px-2 md:px-2.5 xl:px-4 h-full flex flex-col': variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy',
      'ltr:pl-0 rtl:pr-0': variant === 'gridSlim',
      'px-4 lg:px-5 2xl:px-4': variant === 'listSmall'
    }, contactClassName)}>
        {(variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy') && <div className='flex items-center py-2 gap-x-2'>
              <svg className='w-4 h-4 sm:w-6 sm:h-6 text-[#FBD103]' fill='currentColor' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
              </svg>
              <span className='text-xs font-semibold truncate sm:text-sm text-heading dark:text-white'>
                4.5
              </span>
              {product.amount === 0 && <span className='text-xs sm:text-sm leading-5 ltr:pl-3 rtl:pr-3 font-semibold text-[#EF4444]'>
                  {t('text-out-of-stock') || 'Out of stock'}
                </span>}
            </div>}
        {!!(showCategory || showRating) && <div className='flex flex-col md:flex-row md:items-center lg:flex-row xl:flex-row 2xl:flex-row  mb-0.5 items-start'>
            {!!showCategory && <h3 className={cn('font-semibold text-sm mb-1 md:mb-0 ltr:mr-2 rtl:ml-2 ltr:md:mr-3 rtl:md:ml-3', {
          'text-white': bgTransparent,
          'text-black dark:text-neutral-200/70': !bgTransparent
        })}>
                Category
              </h3>}
            {!!showRating && <RatingDisplay rating={5} />}
          </div>}
        {viewCartBtn && <div className="w-full mb-2">
            <Counter quantity={quantity} onIncrement={e => handleIncrement(e)} onDecrement={e => handleDecrement(e)} variant="rounded" />
          </div>}
        <h2 className={cn('truncate mb-1', {
        'text-sm md:text-base': variant === 'grid',
        'font-semibold': demoVariant !== 'ancient',
        'font-bold': demoVariant === 'ancient',
        'text-xs sm:text-sm md:text-base': variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy',
        'md:mb-1.5 text-sm sm:text-base md:text-sm lg:text-base xl:text-lg': variant === 'gridSlim',
        'text-sm sm:text-base md:mb-1.5 pb-0': variant === 'listSmall',
        'text-sm sm:text-base md:text-sm lg:text-base xl:text-lg md:mb-1.5': variant === 'list',
        'text-white': bgTransparent,
        'text-heading dark:text-white': !bgTransparent
      })}>
          {product?.name}
        </h2>
        {!hideProductDescription && product?.description && <p className='text-body dark:text-gray-300 text-xs lg:text-sm leading-normal xl:leading-relaxed max-w-[250px] truncate'>
            {product?.description}
          </p>}
        <div className={`font-semibold text-sm sm:text-base mt-1.5 flex flex-wrap gap-x-2 ${variant === 'grid' ? 'lg:text-lg lg:mt-2.5' : 'sm:text-xl md:text-base lg:text-xl md:text-xl md:mt-2.5 2xl:mt-3'}
          ${variant === 'gridModern' || variant === 'gridModernWide' || variant === 'gridTrendy' ? 'flex flex-col-reverse !gap-x-0 !mt-auto' : ''} ${bgTransparent ? 'text-white' : 'text-heading dark:text-white'}`}>
          <span className={`inline-block ${demoVariant === 'ancient' && 'font-bold text-gray-900 text-lg'}`}>
            {product.strPrice}
          </span>
          {discount && <del className={`sm:text-base font-normal ${bgTransparent ? 'text-white/70' : 'text-gray-800 dark:text-gray-400'}`}>
              {product.strPrice}
            </del>}
          {!viewCartBtn && product.amount > 0 && <button onClick={e => {
          e.stopPropagation();
          addToCart();
        }} className="ml-auto cursor-pointer opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300" type="button">
              <CartIcon className="w-5 h-5 md:w-6 md:h-6" />
            </button>}
        </div>
      </div>

      {(variant === 'gridTrendy' || variant === 'gridModern') && <div className='absolute flex ltr:right-2 rtl:left-2 bottom-2 gap-x-2'>
          <ProductWishIcon className='transition ease-in duration-300 sm:opacity-0 group-hover:opacity-100 delay-200 w-[35px] sm:w-[42px] lg:w-[52px] bg-[#F1F3F4] dark:bg-zinc-950 rounded-md' />
        </div>}
    </div>;
};
export default ProductCard;